// Copyright (c) 2025 Audere, All Rights Reserved

import { useForm, Controller } from "react-hook-form";
import {
  Tab,
  Tabs,
  Box,
  Button,
  TextField,
  MenuItem,
  Stack,
  Typography,
  FormHelperText,
  FormControl,
  FormLabel,
  Alert,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { generatePath, useLocation, useNavigate } from "react-router";
import { useSelector } from "react-redux";

import { createStyleSheet } from "../utils/styles";
import { theme } from "../../theme";
import { isValidString } from "../utils/string";
import { selectCurrentOrganizationId } from "../features/auth/authSlice";
import { DASHBOARD_PATH } from "../utils/constants";
import type { ManageSdkTabsProps } from "../types/projects";
import {
  useBuildApplicationMutation,
  useCreateApplicationMutation,
} from "../features/data/projectApiSlice";
import { getErrorText } from "../utils/error";

const ManageSdkTabs = (props: ManageSdkTabsProps) => {
  const { previousApps, onAppSelect } = props;
  const { t } = useTranslation("ManageSdkTabs");
  const navigate = useNavigate();
  const location = useLocation();
  const currentOrganizationId = useSelector(selectCurrentOrganizationId);
  const { testUid, projectUid } = location.state || {};

  const [createApplication, { error: createApplicationError, isError: isCreateApplicationError }] =
    useCreateApplicationMutation();
  const [buildApplication, { error: buildApplicationError, isError: isBuildApplicationError }] =
    useBuildApplicationMutation();

  const [activeTab, setActiveTab] = useState(0);

  const identifyForm = useForm({
    defaultValues: { appName: "", appId: "" },
    mode: "onChange",
  });
  const previousForm = useForm({
    defaultValues: { app: "" },
    mode: "onChange",
  });

  const navigateAndOpenGenerateDialog = useCallback(() => {
    navigate(generatePath(DASHBOARD_PATH, { orgUid: currentOrganizationId, projectUid, testUid }), {
      state: { openGenerateSdkDialog: true },
    });
  }, [currentOrganizationId, navigate, projectUid, testUid]);
  const handleIdentifyAppFormSubmit = useCallback(() => {
    createApplication({ ...identifyForm.getValues(), projectUid }).then(response => {
      if (response.data?.application_uid) {
        buildApplication({ applicationUid: response.data?.application_uid }).then(response => {
          if (response.data?.app_build?.app_build_uid) {
            navigateAndOpenGenerateDialog();
          }
        });
      }
    });
  }, [
    projectUid,
    createApplication,
    identifyForm,
    buildApplication,
    navigateAndOpenGenerateDialog,
  ]);
  const handlePreviousAppFormSubmit = useCallback(() => {
    buildApplication({ applicationUid: previousForm.getValues().app }).then(response => {
      if (response.data?.app_build?.app_build_uid) {
        navigateAndOpenGenerateDialog();
      }
    });
  }, [buildApplication, navigateAndOpenGenerateDialog, previousForm]);

  const handleCancelClick = useCallback(() => {
    navigate(generatePath(DASHBOARD_PATH, { orgUid: currentOrganizationId, projectUid, testUid }));
  }, [navigate, currentOrganizationId, projectUid, testUid]);
  const handleAppSelect = useCallback(
    (applicationUid: string) => {
      const selectedApp = previousApps?.find(app => app.application_uid === applicationUid);
      if (selectedApp) {
        onAppSelect(selectedApp);
      }
    },
    [previousApps, onAppSelect]
  );

  useEffect(() => {
    const applicationUid = previousApps?.[0]?.application_uid;
    if (applicationUid) {
      onAppSelect(previousApps?.[0]);
      previousForm.setValue("app", applicationUid, { shouldValidate: true });
    }
  }, [previousApps, previousForm, onAppSelect]);

  return (
    <Box sx={styles.container}>
      {(isCreateApplicationError || isBuildApplicationError) && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {getErrorText(buildApplicationError || createApplicationError)}
        </Alert>
      )}
      <Stack>
        <Typography variant="h5" fontWeight={500} gutterBottom>
          {t("identifyHeader")}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={styles.identifySubheader}>
          {t("identifySubheader")}
        </Typography>
      </Stack>
      <Tabs
        variant="fullWidth"
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        sx={styles.tabContainer}
      >
        <Tab sx={styles.identifyAppTab} label={t("tabs.identifyApp")} />
        <Tab sx={styles.previousAppTab} label={t("tabs.previousApp")} />
      </Tabs>
      {activeTab === 0 && (
        <Box sx={styles.formContainer}>
          <form onSubmit={identifyForm.handleSubmit(handleIdentifyAppFormSubmit)}>
            <FormControl fullWidth sx={styles.inputFormControl}>
              <FormLabel sx={styles.inputFormLabel} htmlFor="appName">
                {t("forms.identifyApp.appName")}{" "}
                <Typography component="span" color="error">
                  *
                </Typography>
              </FormLabel>
              <Controller
                name="appName"
                control={identifyForm.control}
                rules={{
                  required: t("errorMessage"),
                  validate: value => isValidString(value) || t("errorMessage"),
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    id="appName"
                    fullWidth
                    error={!!identifyForm.formState.errors.appName}
                    helperText={identifyForm.formState.errors.appName?.message}
                    placeholder={t("forms.identifyApp.appNamePlaceholder")}
                    sx={styles.appNameInput}
                  />
                )}
              />
            </FormControl>
            <FormControl fullWidth sx={styles.inputFormControl}>
              <FormLabel sx={styles.inputFormLabel} htmlFor="appId">
                {t("forms.identifyApp.appId")}{" "}
                <Typography component="span" color="error">
                  *
                </Typography>
              </FormLabel>
              <Controller
                name="appId"
                control={identifyForm.control}
                rules={{
                  required: t("errorMessage"),
                  validate: value => isValidString(value) || t("errorMessage"),
                }}
                render={({ field }) => (
                  <>
                    <TextField
                      {...field}
                      id="appId"
                      fullWidth
                      error={!!identifyForm.formState.errors.appId}
                      helperText={identifyForm.formState.errors.appId?.message}
                      placeholder={t("forms.identifyApp.appIdPlaceholder")}
                    />
                    <FormHelperText>{t("forms.identifyApp.appIdHelperText")}</FormHelperText>
                  </>
                )}
              />
            </FormControl>
            <Stack flexDirection="row" justifyContent="end" alignItems="center" gap={2} mt={4}>
              <Button type="button" variant="text" onClick={handleCancelClick}>
                {t("common:cancel")}
              </Button>
              <Button type="submit" variant="contained" disabled={!identifyForm.formState.isValid}>
                {t("common:generateSdk")}
              </Button>
            </Stack>
          </form>
        </Box>
      )}
      {activeTab === 1 && (
        <Box sx={styles.formContainer}>
          <form onSubmit={previousForm.handleSubmit(handlePreviousAppFormSubmit)}>
            <FormControl fullWidth sx={styles.inputFormControl}>
              <FormLabel htmlFor="appSelect">{t("forms.previousApp.dropdownLabel")}</FormLabel>
              <Controller
                name="app"
                control={previousForm.control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    id="appSelect"
                    select
                    fullWidth
                    margin="normal"
                    slotProps={{ select: { displayEmpty: true } }}
                    onChange={e => {
                      field.onChange(e);
                      handleAppSelect(e.target.value);
                    }}
                  >
                    <MenuItem disabled value="">
                      {t("forms.previousApp.placeholder")}
                    </MenuItem>
                    {previousApps?.map(app => (
                      <MenuItem key={app.application_uid} value={app.application_uid}>
                        {app.name}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </FormControl>
            <Stack flexDirection="row" justifyContent="end" alignItems="center" gap={2} mt={4}>
              <Button type="button" variant="text" onClick={handleCancelClick}>
                {t("common:cancel")}
              </Button>
              <Button type="submit" variant="contained" disabled={!previousForm.formState.isValid}>
                {t("common:generateSdk")}
              </Button>
            </Stack>
          </form>
        </Box>
      )}
    </Box>
  );
};

export default ManageSdkTabs;

const styles = createStyleSheet({
  container: {
    flex: 1,
    padding: theme.spacing(3),
    background: theme.palette.common.white,
    border: `1px solid ${theme.palette.grey[400]}`,
    maxWidth: theme.spacing(68),
  },
  tabContainer: {
    mt: 4,
    "& .MuiButtonBase-root": {
      textTransform: "none",
      fontWeight: 400,
      fontSize: "16px",
      border: `1px solid ${theme.palette.grey[300]}`,
    },
    "& .MuiTab-root.Mui-selected": {
      background: theme.palette.primary.main,
      color: theme.palette.common.white,
      border: `1px solid ${theme.palette.primary.main}`,
    },
    "& .MuiTabs-indicator": {
      display: "none",
    },
  },
  identifyAppTab: {
    borderTopLeftRadius: "8px",
    borderBottomLeftRadius: "8px",
    borderRight: "none",
  },
  previousAppTab: {
    borderTopRightRadius: "8px",
    borderBottomRightRadius: "8px",
    borderLeft: "none",
  },
  inputFormControl: { px: 0 },
  inputFormLabel: { mb: 1 },
  identifySubheader: { mt: 1 },
  formContainer: { mt: 2 },
  appNameInput: { mb: 2 },
});
